import type { AccountInfo, SessionUserInfo, SharedSessionData } from '../types';

import {
  ACCOUNT_QUERY,
  DATA_BROADCAST_CHANNEL_PREFIX,
  ESTABLISH_BROADCAST_CHANNEL_PREFIX,
  GLOBAL_STATE_CACHE_PREFIX,
  MULTITAB_LOCALSTORAGE_KEY_PREFIX,
  SESSION_ACCOUNT_PREFIX,
} from '../config';
import { IS_MULTIACCOUNT_SUPPORTED } from './browser/globalEnvironment';
import { getStorageAdapter, STORAGE_KEYS, getChunkedItem, setChunkedItem } from './storage';

const WORKER_NAME = typeof WorkerGlobalScope !== 'undefined' && globalThis.self instanceof WorkerGlobalScope
  ? globalThis.self.name : undefined;
const WORKER_ACCOUNT_SLOT = WORKER_NAME ? Number(new URLSearchParams(WORKER_NAME).get(ACCOUNT_QUERY)) : undefined;

export const ACCOUNT_SLOT = WORKER_ACCOUNT_SLOT || (
  IS_MULTIACCOUNT_SUPPORTED ? getAccountSlot(globalThis.location.href) : undefined
);

export const DATA_BROADCAST_CHANNEL_NAME = `${DATA_BROADCAST_CHANNEL_PREFIX}_${ACCOUNT_SLOT || 1}`;
export const ESTABLISH_BROADCAST_CHANNEL_NAME = `${ESTABLISH_BROADCAST_CHANNEL_PREFIX}_${ACCOUNT_SLOT || 1}`;
export const MULTITAB_STORAGE_KEY = `${MULTITAB_LOCALSTORAGE_KEY_PREFIX}_${ACCOUNT_SLOT || 1}`;
export const GLOBAL_STATE_CACHE_KEY = ACCOUNT_SLOT
  ? `${GLOBAL_STATE_CACHE_PREFIX}_${ACCOUNT_SLOT}` : GLOBAL_STATE_CACHE_PREFIX;

export function getAccountSlot(url: string) {
  const params = new URL(url).searchParams;
  const slot = params.get(ACCOUNT_QUERY);
  const slotNumber = slot ? Number(slot) : 1;
  if (!slotNumber || Number.isNaN(slotNumber) || slotNumber === 1) return undefined;
  return slotNumber;
}

/**
 * Synchronous version of getAccountsInfo for use in hooks and selectors
 * Only reads from localStorage, not CloudStorage
 */
export function getAccountsInfo(): Record<number, AccountInfo> {
  if (!IS_MULTIACCOUNT_SUPPORTED) return {};

  const allKeys = Object.keys(localStorage);
  const allSlots = allKeys.filter((key) => key.startsWith(SESSION_ACCOUNT_PREFIX));
  const accountInfo: Record<number, AccountInfo> = {};

  for (const key of allSlots) {
    const i = Number(key.slice(SESSION_ACCOUNT_PREFIX.length));
    const info = getAccountInfoSync(i);
    if (info) {
      accountInfo[i] = info;
    }
  }

  return accountInfo;
}

export function getFirstLoggedInAccountSlot(accounts = getAccountsInfo()) {
  return Object.keys(accounts).map(Number).sort((a, b) => a - b)[0];
}

/**
 * Async version of getAccountsInfo that reads through the storage adapter
 */
export async function getAccountsInfoAsync(): Promise<Record<number, AccountInfo>> {
  if (!IS_MULTIACCOUNT_SUPPORTED) return {};

  try {
    const adapter = getStorageAdapter();
    const allKeys = await adapter.getKeys();

    // Filter for account meta keys
    const accountMetaKeys = allKeys.filter((key) => key.match(/^tt_v1_account_\d+_meta$/));
    const accountInfo: Record<number, AccountInfo> = {};

    for (const key of accountMetaKeys) {
      const match = key.match(/^tt_v1_account_(\d+)_meta$/);
      if (!match) continue;

      const slot = Number(match[1]);
      const info = await getAccountInfo(slot);
      if (info) {
        accountInfo[slot] = info;
      }
    }

    // Fallback to localStorage for migration
    if (Object.keys(accountInfo).length === 0) {
      return getAccountsInfo();
    }

    return accountInfo;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[getAccountsInfoAsync] Failed to get accounts', error);
    return getAccountsInfo(); // Fallback to sync version
  }
}

/**
 * Synchronous version - only reads from localStorage
 */
function getAccountInfoSync(slot: number): AccountInfo | undefined {
  try {
    const legacyData = localStorage.getItem(`${SESSION_ACCOUNT_PREFIX}${slot}`);
    if (!legacyData) return undefined;

    const sessionData = JSON.parse(legacyData) as SharedSessionData;
    const {
      userId, avatarUri, color, emojiStatusId, firstName, lastName, isPremium, isTest, phone,
    } = sessionData;

    if (!userId) return undefined;

    return {
      userId,
      avatarUri,
      color,
      emojiStatusId,
      firstName,
      lastName,
      isPremium,
      isTest,
      phone,
    };
  } catch (error) {
    return undefined;
  }
}

/**
 * Async version - reads from CloudStorage with fallback to localStorage
 */
async function getAccountInfo(slot: number): Promise<AccountInfo | undefined> {
  try {
    // Always get avatarUri from localStorage (not synced via CloudStorage)
    const localInfo = getAccountInfoSync(slot);
    const localAvatarUri = localInfo?.avatarUri;

    const adapter = getStorageAdapter();

    // Try to get metadata first (lightweight)
    const metaStr = await adapter.getItem(STORAGE_KEYS.accountMeta(slot));
    if (metaStr) {
      const meta = JSON.parse(metaStr);
      const { userId } = meta;
      if (!userId) return undefined;

      // Merge with local avatarUri
      return {
        ...meta,
        avatarUri: localAvatarUri,
      };
    }

    // Fallback to full auth data
    const authData = await getChunkedItem(adapter, STORAGE_KEYS.accountAuth(slot));
    if (!authData) {
      // Fallback to localStorage
      return localInfo;
    }

    const sessionData = JSON.parse(authData) as SharedSessionData;
    const {
      userId, color, emojiStatusId, firstName, lastName, isPremium, isTest, phone,
    } = sessionData;

    if (!userId) return undefined;

    return {
      userId,
      avatarUri: localAvatarUri, // Always use local avatarUri
      color,
      emojiStatusId,
      firstName,
      lastName,
      isPremium,
      isTest,
      phone,
    };
  } catch (error) {
    return getAccountInfoSync(slot);
  }
}

export async function storeAccountData(
  slot: number | undefined,
  data: Partial<SessionUserInfo>,
): Promise<void> {
  try {
    const adapter = getStorageAdapter();

    // Load current slot data
    const authData = await getChunkedItem(adapter, STORAGE_KEYS.accountAuth(slot || 1));
    if (!authData) {
      // Fallback to localStorage
      const legacyData = localStorage.getItem(`${SESSION_ACCOUNT_PREFIX}${slot || 1}`);
      if (!legacyData) return;

      const currentSlotData = JSON.parse(legacyData) as SharedSessionData;
      const updatedSharedData: SharedSessionData = {
        ...currentSlotData,
        ...data,
      };

      if (!updatedSharedData.userId) return;
      await writeSlotSession(slot, updatedSharedData);
      return;
    }

    const currentSlotData = JSON.parse(authData) as SharedSessionData;
    const updatedSharedData: SharedSessionData = {
      ...currentSlotData,
      ...data,
    };

    if (!updatedSharedData.userId) return;

    await writeSlotSession(slot, updatedSharedData);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[storeAccountData] Failed to store account data', error);
  }
}

export async function writeSlotSession(
  slot: number | undefined,
  data: SharedSessionData,
): Promise<void> {
  try {
    const adapter = getStorageAdapter();
    const slotNum = slot || 1;

    // Extract metadata for fast access
    // Note: avatarUri excluded - it's base64 image that can be too large for CloudStorage
    const meta = {
      userId: data.userId,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      color: data.color,
      isPremium: data.isPremium,
      emojiStatusId: data.emojiStatusId,
    };

    // Write metadata (small, no chunking) - only if not too large
    try {
      await adapter.setItem(STORAGE_KEYS.accountMeta(slotNum), JSON.stringify(meta));
    } catch (metaError) {
      // If meta fails (shouldn't happen), continue - it's just an optimization
      // eslint-disable-next-line no-console
      console.warn('[writeSlotSession] Failed to save meta, continuing', metaError);
    }

    // Write full auth data (may need chunking)
    // Note: avatarUri excluded from CloudStorage to save space
    const { avatarUri, ...dataWithoutAvatar } = data;
    await setChunkedItem(adapter, STORAGE_KEYS.accountAuth(slotNum), JSON.stringify(dataWithoutAvatar));

    // Also write to localStorage for backward compatibility during migration
    // localStorage keeps avatarUri for local display
    localStorage.setItem(`${SESSION_ACCOUNT_PREFIX}${slotNum}`, JSON.stringify(data));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[writeSlotSession] Failed to write session', error);
    throw error;
  }
}

export function getAccountSlotUrl(slot: number, forLogin?: boolean, isTest?: boolean) {
  const url = new URL(globalThis.location.href);
  if (slot !== 1) {
    url.searchParams.set(ACCOUNT_QUERY, String(slot));
  } else {
    url.searchParams.delete(ACCOUNT_QUERY);
  }

  if (isTest) {
    url.searchParams.set('test', 'true');
  } else {
    url.searchParams.delete('test');
  }

  url.hash = forLogin ? 'login' : '';
  return url.toString();
}

// Validate current version across all tabs to avoid conflicts
if (typeof window === 'object') {
  const versionChannel = new BroadcastChannel('tt-version');
  versionChannel.postMessage({ version: APP_VERSION });

  versionChannel.addEventListener('message', (event) => {
    const { version } = event.data;
    if (!version) return;
    if (semverCompare(APP_VERSION, version) === -1) {
      window.location.reload();
    }

    // If incoming version is older, send back the current version
    if (semverCompare(APP_VERSION, version) === 1) {
      versionChannel.postMessage({ version: APP_VERSION });
    }
  });
}

function semverCompare(a: string, b: string) {
  if (a.startsWith(`${b}-`)) return -1;
  if (b.startsWith(`${a}-`)) return 1;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'case', caseFirst: 'upper' });
}
